// Drag edge auto-scroll — Walter 2026-07-04.
//
// While a drag is active, if the pointer sits within `edge` px of a scroll
// container's edge, scroll that container a little each animation frame so the
// user can drop somewhere that's currently off-screen without letting go,
// scrolling by hand, and re-grabbing. Speed ramps up the deeper the pointer is
// into the edge zone.
//
// Works for ANY drag mechanism (manual mouse-move, touch long-press) because
// it's pointer-agnostic: the caller feeds it the pointer position via
// update(x, y) on each move and calls stop() when the drag ends.
//
// Key property: once the pointer is parked in the edge zone the RAF loop keeps
// scrolling on its own — it does NOT depend on a continuous stream of move
// events (holding the finger/cursor still at the edge still scrolls).
//
// Usage:
//   const scroller = createDragAutoScroll({
//     getContainer: () => wrapRef.current,
//     onScroll: () => recomputeDropTarget(),   // scroll changed hit-testing
//   });
//   // in the drag move handler: scroller.update(clientX, clientY)
//   // on drag end:              scroller.stop()
// Walk up from `el` to the nearest ancestor that actually scrolls (overflow
// auto/scroll on either axis). Used so callers don't have to hard-code the
// scroll-container nesting (e.g. the free canvas: tile → content div →
// scrollable canvas).
export function nearestScrollable(el) {
  let n = el && el.parentElement;
  while (n) {
    const s = getComputedStyle(n);
    if (/(auto|scroll)/.test(`${s.overflowY} ${s.overflowX}`)) return n;
    n = n.parentElement;
  }
  return null;
}

export function createDragAutoScroll({ getContainer, onScroll, edge = 48, maxSpeed = 16 } = {}) {
  let raf = null;
  let px = 0;
  let py = 0;
  let active = false;

  // depth = how far (px) the pointer is INSIDE the edge zone (0..edge).
  // Ramp from a gentle 2px/frame at the boundary up to maxSpeed at the very
  // edge, so a small overshoot nudges slowly and parking hard against the
  // edge scrolls briskly.
  const speedFor = (depth) => {
    const t = Math.max(0, Math.min(1, depth / edge));
    return Math.ceil(2 + t * (maxSpeed - 2));
  };

  const tick = () => {
    raf = null;
    if (!active) return;
    const el = typeof getContainer === 'function' ? getContainer() : getContainer;
    if (!el) return;
    const r = el.getBoundingClientRect();
    let dx = 0;
    let dy = 0;
    if (py < r.top + edge) dy = -speedFor(r.top + edge - py);
    else if (py > r.bottom - edge) dy = speedFor(py - (r.bottom - edge));
    if (px < r.left + edge) dx = -speedFor(r.left + edge - px);
    else if (px > r.right - edge) dx = speedFor(px - (r.right - edge));
    // Pointer left every edge zone → let the loop go idle until the next move.
    if (dx === 0 && dy === 0) return;
    const beforeT = el.scrollTop;
    const beforeL = el.scrollLeft;
    el.scrollBy({ left: dx, top: dy });
    // Only bother the caller (drop-target re-hit-test) when we actually moved —
    // avoids churn once the container is scrolled to its extent.
    if (onScroll && (el.scrollTop !== beforeT || el.scrollLeft !== beforeL)) onScroll();
    raf = requestAnimationFrame(tick); // keep scrolling while parked in the zone
  };

  return {
    update(clientX, clientY) {
      px = clientX;
      py = clientY;
      active = true;
      if (raf == null) raf = requestAnimationFrame(tick);
    },
    stop() {
      active = false;
      if (raf != null) {
        cancelAnimationFrame(raf);
        raf = null;
      }
    },
  };
}
